"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  type Option,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type CreditRow,
  RoleGuide,
  RoleMatrix,
  setRole,
} from "@/components/tools/publishing-statements/credit-matrix";
import {
  ExampleButton,
  NoticeList,
  SmallButton,
  StatementCard,
} from "@/components/tools/publishing-statements/kit";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type NameStyle,
  sharedInitials,
} from "@/lib/tool-kit/publishing/authors";
import {
  CREDIT_ROLE_IDS,
  type CreditDegree,
  type CreditLayout,
  type CreditRoleId,
  contributorsWithoutRoles,
  creditEntries,
  creditParagraphs,
  unassignedRoles,
} from "@/lib/tool-kit/publishing/credit";

/** English strings. A translated content file overrides these through `ui`. */
export const CREDIT_AUTHOR_STATEMENT_GENERATOR_STRINGS = {
  authorsLegend: "Authors, in byline order",
  authorLabel: "Author {number}",
  namePlaceholder: "Full name, e.g. Maya Okafor",
  addAuthor: "Add author",
  remove: "Remove",
  removeAria: "Remove author {number}",
  matrixCaption: "Tick every role each author performed.",
  roleColumn: "Role",
  cellAria: "{role}: {author}",
  degreeNone: "—",
  degreeYes: "Contributed",
  layoutLabel: "Layout",
  layoutAuthor: "By author (Elsevier style)",
  layoutRole: "By role (PLOS style)",
  namesLabel: "Names",
  namesFull: "Full names",
  namesInitials: "Initials",
  degreesToggle: "Add the degree of contribution (lead, equal, supporting)",
  perLineToggle: "Put each entry on its own line",
  degreesNote:
    "The degree of contribution is optional and is not part of the CRediT standard itself — include it only if your journal asks for it.",
  roleGuide: "What each role means",
  roleHintSeparator: " — ",
  tryExample: "Try an example",
  exampleNames: ["Maya Okafor", "Daniel Ruiz", "Priya Raman"],
  results: "Your CRediT statement",
  statementHeading: "CRediT author statement",
  copy: "Copy statement",
  copied: "Copied!",
  prompt:
    "Enter at least one author's name and tick a role to build the statement.",
  noRolesWarning:
    "one: {names} has no role yet — every contributor you list needs at least one.|other: {names} have no role yet — every contributor you list needs at least one.",
  sharedInitialsWarning:
    "More than one author has the initials {initials}. Use full names, or tell them apart by hand (e.g. J.Sm. and J.St.).",
  unassignedInfo:
    "Nobody holds: {roles}. That is fine if a role did not apply to this study.",
  roles: {
    conceptualization: "Conceptualization",
    dataCuration: "Data curation",
    formalAnalysis: "Formal analysis",
    fundingAcquisition: "Funding acquisition",
    investigation: "Investigation",
    methodology: "Methodology",
    projectAdministration: "Project administration",
    resources: "Resources",
    software: "Software",
    supervision: "Supervision",
    validation: "Validation",
    visualization: "Visualization",
    writingOriginalDraft: "Writing – original draft",
    writingReviewEditing: "Writing – review & editing",
  },
  roleHints: {
    conceptualization: "Shaping the research idea and its goals and aims.",
    dataCuration:
      "Annotating, cleaning and maintaining the data — and any code needed to read it — for use and reuse.",
    formalAnalysis:
      "Statistical, mathematical, computational or other formal analysis of the data.",
    fundingAcquisition: "Securing the money that paid for the project.",
    investigation:
      "Running the experiments or collecting the data or evidence.",
    methodology: "Developing or designing the methods; building models.",
    projectAdministration:
      "Managing and coordinating the planning and running of the research.",
    resources:
      "Providing materials, samples, patients, animals, instruments, computing or other tools.",
    software:
      "Writing, implementing and testing the code and algorithms the study relied on.",
    supervision:
      "Oversight and leadership of the research, including mentoring outside the core team.",
    validation:
      "Checking that the results and experiments can be replicated or reproduced.",
    visualization:
      "Preparing the figures and other visual presentation of data.",
    writingOriginalDraft:
      "Writing the first draft, including substantive translation.",
    writingReviewEditing:
      "Critical review, commentary or revision by members of the research group, before or after publication.",
  },
  degrees: { lead: "lead", equal: "equal", supporting: "supporting" },
  degreeLabels: { lead: "Lead", equal: "Equal", supporting: "Supporting" },
  withDegree: "{item} ({degree})",
  itemSeparator: ", ",
  labelEnd: ":",
  entryEnd: ".",
  listSeparator: ", ",
} as const;

type Strings = typeof CREDIT_AUTHOR_STATEMENT_GENERATOR_STRINGS;

type AuthorRow = CreditRow;

const MAX_AUTHORS = 40;

/** The example's roles, author by author — structure, not words. */
const EXAMPLE_ROLES: Partial<Record<CreditRoleId, CreditDegree>>[] = [
  {
    conceptualization: "lead",
    methodology: "lead",
    formalAnalysis: "lead",
    writingOriginalDraft: "lead",
    fundingAcquisition: "supporting",
  },
  {
    dataCuration: "lead",
    investigation: "lead",
    software: "lead",
    visualization: "lead",
    writingReviewEditing: "equal",
  },
  {
    conceptualization: "supporting",
    fundingAcquisition: "lead",
    projectAdministration: "lead",
    supervision: "lead",
    writingReviewEditing: "equal",
  },
];

function emptyRow(id: number): AuthorRow {
  return { id, name: "", roles: {} };
}

function warningsFor(
  rows: readonly AuthorRow[],
  nameStyle: NameStyle,
  strings: Strings,
  locale: string
): string[] {
  const notes: string[] = [];
  const missing = contributorsWithoutRoles(rows);
  if (missing.length > 0) {
    notes.push(
      tPlural(strings.noRolesWarning, locale, missing.length, {
        names: missing.join(strings.listSeparator),
      }).trim()
    );
  }
  const shared = sharedInitials(rows.map((row) => row.name));
  if (nameStyle === "initials" && shared.length > 0) {
    notes.push(
      t(strings.sharedInitialsWarning, {
        initials: shared.join(strings.listSeparator),
      })
    );
  }
  const unassigned = unassignedRoles(rows);
  if (unassigned.length > 0 && unassigned.length < CREDIT_ROLE_IDS.length) {
    notes.push(
      t(strings.unassignedInfo, {
        roles: unassigned
          .map((role) => strings.roles[role])
          .join(strings.listSeparator),
      })
    );
  }
  return notes;
}

export default function CreditAuthorStatementGeneratorWidget() {
  const strings = useToolUi(CREDIT_AUTHOR_STATEMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [rows, setRows] = useState<AuthorRow[]>([emptyRow(1), emptyRow(2)]);
  const [nextId, setNextId] = useState(3);
  const [layout, setLayout] = useState<CreditLayout>("author");
  const [nameStyle, setNameStyle] = useState<NameStyle>("full");
  const [showDegrees, setShowDegrees] = useState(false);
  const [perLine, setPerLine] = useState(false);

  const entries = creditEntries(
    rows,
    { layout, nameStyle, showDegrees },
    strings
  );
  const paragraphs = creditParagraphs(entries, strings, perLine);
  const notes = warningsFor(rows, nameStyle, strings, locale);

  function update(id: number, change: (row: AuthorRow) => AuthorRow) {
    setRows((current) =>
      current.map((row) => (row.id === id ? change(row) : row))
    );
  }

  function addAuthor() {
    setRows((current) => [...current, emptyRow(nextId)]);
    setNextId((id) => id + 1);
  }

  function loadExample() {
    const names = strings.exampleNames;
    setRows(
      names.map((name, index) => ({
        id: nextId + index,
        name,
        roles: { ...EXAMPLE_ROLES[index % EXAMPLE_ROLES.length] },
      }))
    );
    setNextId((id) => id + names.length);
  }

  const layoutOptions: Option<CreditLayout>[] = [
    { value: "author", label: strings.layoutAuthor },
    { value: "role", label: strings.layoutRole },
  ];
  const nameOptions: Option<NameStyle>[] = [
    { value: "full", label: strings.namesFull },
    { value: "initials", label: strings.namesInitials },
  ];

  return (
    <div className="space-y-6">
      <fieldset className="space-y-3">
        <legend className={LABEL}>{strings.authorsLegend}</legend>
        {rows.map((row, index) => (
          <div className="flex items-end gap-3" key={row.id}>
            <div className="flex-1">
              <TextField
                id={`cas-author-${row.id}`}
                label={t(strings.authorLabel, { number: index + 1 })}
                onChange={(name) => update(row.id, (r) => ({ ...r, name }))}
                placeholder={strings.namePlaceholder}
                value={row.name}
              />
            </div>
            <SmallButton
              ariaLabel={t(strings.removeAria, { number: index + 1 })}
              disabled={rows.length <= 1}
              label={strings.remove}
              onClick={() =>
                setRows((current) => current.filter((r) => r.id !== row.id))
              }
            />
          </div>
        ))}
        <div className="flex flex-wrap gap-3">
          <SmallButton
            disabled={rows.length >= MAX_AUTHORS}
            label={strings.addAuthor}
            onClick={addAuthor}
          />
        </div>
      </fieldset>

      <RoleMatrix
        onChange={(id, role, degree) =>
          update(id, (row) => setRole(row, role, degree))
        }
        rows={rows}
        showDegrees={showDegrees}
        strings={strings}
      />
      <RoleGuide strings={strings} />

      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.layoutLabel}
          name="cas-layout"
          onChange={setLayout}
          options={layoutOptions}
          value={layout}
        />
        <PillGroup
          label={strings.namesLabel}
          name="cas-names"
          onChange={setNameStyle}
          options={nameOptions}
          value={nameStyle}
        />
      </div>
      <div className="space-y-2">
        <Checkbox
          checked={showDegrees}
          label={strings.degreesToggle}
          onChange={setShowDegrees}
        />
        <Checkbox
          checked={perLine}
          label={strings.perLineToggle}
          onChange={setPerLine}
        />
        {showDegrees ? <p className={HINT}>{strings.degreesNote}</p> : null}
      </div>
      <ExampleButton label={strings.tryExample} onClick={loadExample} />

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {entries.length > 0 ? (
          <StatementCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            heading={strings.statementHeading}
            paragraphs={paragraphs}
            plainSeparator={"\n"}
          />
        ) : (
          <Notice>{strings.prompt}</Notice>
        )}
        <NoticeList items={notes} />
      </section>
    </div>
  );
}
